import React, { useCallback, useEffect, useMemo, useState, FormEvent } from 'react';
import {
  billingApi,
  BillingUserProjection,
  CompanyBillingStatus,
  InvoiceRecord,
  LicenseRecord,
  SubscriptionRecord,
  tenancyApi,
  UserDto,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePaginatedList } from '../../hooks/usePagination';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconCreditCard, IconPlus, IconSync, IconTrash } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';

export function BillingPage() {
  const { company, user, refreshUserSession } = useAuth();
  const { pushToast } = useToast();

  const [billingStatus, setBillingStatus] = useState<CompanyBillingStatus | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [licenses, setLicenses] = useState<LicenseRecord[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [billingUsers, setBillingUsers] = useState<BillingUserProjection[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // License Search & Filter
  const [licenseSearch, setLicenseSearch] = useState<string>('');
  const [licenseStatusFilter, setLicenseStatusFilter] = useState<'all' | 'active' | 'inactive'>(
    'all',
  );

  // Provision License Modal
  const [createLicenseOpen, setCreateLicenseOpen] = useState<boolean>(false);
  const [skuInput, setSkuInput] = useState<string>('SYNC-PRO-ANNUAL');
  const [quantityInput, setQuantityInput] = useState<number>(1);
  const [creatingLicense, setCreatingLicense] = useState<boolean>(false);

  // Email Invoice Modal
  const [invoiceToEmail, setInvoiceToEmail] = useState<InvoiceRecord | null>(null);
  const [emailRecipientsInput, setEmailRecipientsInput] = useState<string>('');
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState<boolean>(false);

  // Printable Invoice Preview Modal
  const [previewInvoice, setPreviewInvoice] = useState<InvoiceRecord | null>(null);

  // Subscription Watcher & Extra Billing Receivers
  const [watcherEmailInput, setWatcherEmailInput] = useState<string>('');
  const [extraReceiversInput, setExtraReceiversInput] = useState<string>('');
  const [selectedAddBillingUserId, setSelectedAddBillingUserId] = useState<string>('');

  const loadBillingData = useCallback(async () => {
    setLoading(true);
    const companyId = company?.id;
    try {
      const [statusRes, subsRes, licsRes, invsRes] = await Promise.all([
        billingApi.getCompanyBillingStatus(companyId).catch(() => null),
        billingApi.getMySubscriptions(companyId).catch(() => []),
        billingApi.getLicenses(companyId).catch(() => []),
        billingApi.getMyInvoices(companyId).catch(() => []),
      ]);
      setBillingStatus(statusRes);
      setSubscriptions(Array.isArray(subsRes) ? subsRes : []);
      setLicenses(Array.isArray(licsRes) ? licsRes : []);
      setInvoices(Array.isArray(invsRes) ? invsRes : []);

      if (companyId) {
        const [bUsers, cUsers] = await Promise.all([
          billingApi.listBillingUsers(companyId).catch(() => []),
          tenancyApi.listUsers(companyId).catch(() => []),
        ]);
        setBillingUsers(Array.isArray(bUsers) ? bUsers : []);
        setCompanyUsers(Array.isArray(cUsers) ? cUsers : []);
      }
      setExtraReceiversInput((company?.extra_billing_receivers || []).join(', '));
    } finally {
      setLoading(false);
    }
  }, [company?.id, company?.extra_billing_receivers]);

  useEffect(() => {
    void loadBillingData();
  }, [loadBillingData]);

  const activeSub =
    billingStatus?.active_subscription ||
    subscriptions.find((s) => s.status === 'live' || s.status === 'trial') ||
    subscriptions[0] ||
    null;

  const filteredLicenses = useMemo(() => {
    const q = licenseSearch.trim().toLowerCase();
    return licenses.filter((lic) => {
      if (licenseStatusFilter === 'active' && lic.status !== 'active' && lic.status !== 'free_trial') {
        return false;
      }
      if (
        licenseStatusFilter === 'inactive' &&
        (lic.status === 'active' || lic.status === 'free_trial')
      ) {
        return false;
      }
      if (!q) return true;
      const licType = (lic as { type?: string }).type || '';
      return (
        lic.id.toLowerCase().includes(q) ||
        (lic.project_id && lic.project_id.toLowerCase().includes(q)) ||
        (lic.entity_procore_id !== null && String(lic.entity_procore_id).toLowerCase().includes(q)) ||
        lic.sku.toLowerCase().includes(q) ||
        licType.toLowerCase().includes(q) ||
        lic.status.toLowerCase().includes(q)
      );
    });
  }, [licenses, licenseSearch, licenseStatusFilter]);

  const licensesPagination = usePaginatedList(filteredLicenses, 10);
  const invoicesPagination = usePaginatedList(invoices, 10);
  const billingUsersPagination = usePaginatedList(billingUsers, 10);

  const handleCancelOrReactivateSubscription = async () => {
    const companyId = company?.id || billingStatus?.company_id;
    if (!companyId) return;
    try {
      if (activeSub && !activeSub.cancel_at_period_end && activeSub.status !== 'cancelled') {
        await billingApi.cancelSubscription(companyId, activeSub.id);
        pushToast({
          tone: 'warning',
          title: 'Subscription scheduled for cancellation',
          description: 'Your subscription will remain active until the current term ends.',
        });
      } else {
        await billingApi.reactivateSubscription(companyId, activeSub?.id);
        pushToast({
          tone: 'success',
          title: 'Subscription reactivated',
          description: 'Auto-renewal has been restored.',
        });
      }
      await loadBillingData();
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Subscription update failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleOpenHostedPaymentPage = async () => {
    const companyId = company?.id || billingStatus?.company_id || 'default-company';
    try {
      const hosted = await billingApi.getHpUpdateCard(activeSub?.id, companyId);
      window.open(hosted.url, '_blank', 'noopener,noreferrer');
      pushToast({
        tone: 'info',
        title: 'Opened hosted payment portal',
        description: `Session ${hosted.hosted_page_id}`,
      });
    } catch {
      const hosted = billingApi.getHostedCardUpdateUrl(
        companyId,
        activeSub?.plan_code || 'SYNC-STD-MONTHLY',
      );
      window.open(hosted.url, '_blank', 'noopener,noreferrer');
      pushToast({
        tone: 'info',
        title: 'Opened hosted payment portal',
        description: `Session ${hosted.hosted_page_id}`,
      });
    }
  };

  const handleProvisionLicense = async (e: FormEvent) => {
    e.preventDefault();
    setCreatingLicense(true);
    try {
      const created = await billingApi.createStandardLicense({
        company_id: company?.id,
        sku: skuInput.trim(),
        quantity: quantityInput,
      });
      setLicenses((prev) => [created, ...prev]);
      setCreateLicenseOpen(false);
      await loadBillingData();
      pushToast({
        tone: 'success',
        title: 'Standard license provisioned',
        description: `SKU ${created.sku} (qty ${created.quantity}) is now ${created.status}.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not provision license',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingLicense(false);
    }
  };

  const handleAddWatcher = async (e: FormEvent) => {
    e.preventDefault();
    const companyId = company?.id || billingStatus?.company_id;
    if (!companyId || !watcherEmailInput.trim()) return;
    try {
      await billingApi.watchSubscription(companyId, watcherEmailInput.trim(), activeSub?.id);
      setWatcherEmailInput('');
      await loadBillingData();
      pushToast({
        tone: 'success',
        title: 'Added subscription watcher',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to add watcher',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleRemoveWatcher = async (email: string) => {
    const companyId = company?.id || billingStatus?.company_id;
    if (!companyId) return;
    try {
      await billingApi.stopWatchSubscription(companyId, email, activeSub?.id);
      await loadBillingData();
      pushToast({
        tone: 'info',
        title: 'Removed subscription watcher',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to remove watcher',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleAddBillingContact = async (e: FormEvent) => {
    e.preventDefault();
    if (!company?.id || !selectedAddBillingUserId) return;
    try {
      await billingApi.addBillingUser(company.id, selectedAddBillingUserId);
      setSelectedAddBillingUserId('');
      await loadBillingData();
      pushToast({
        tone: 'success',
        title: 'Billing permission granted',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not add billing contact',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleSetPrimaryBillingUser = async (userId: string) => {
    if (!company?.id) return;
    try {
      await billingApi.setPrimaryBillingUser(company.id, userId);
      await tenancyApi.updateCompanyPbr({ company_id: company.id, pbr_user_id: userId });
      await loadBillingData();
      await refreshUserSession();
      pushToast({
        tone: 'success',
        title: 'Primary Billing Receiver (PBR) updated',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to set Primary Billing Receiver',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleRemoveBillingContact = async (userId: string) => {
    if (!company?.id) return;
    try {
      await billingApi.removeBillingUser(company.id, userId);
      await loadBillingData();
      pushToast({
        tone: 'info',
        title: 'Billing contact removed',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not remove billing contact',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleSaveExtraReceivers = async (e: FormEvent) => {
    e.preventDefault();
    const emails = extraReceiversInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await tenancyApi.updateExtraBillingReceivers({
        company_id: company?.id,
        extra_billing_receivers: emails,
      });
      await refreshUserSession();
      pushToast({
        tone: 'success',
        title: 'Extra billing email receivers updated',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to update billing receivers',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleSendInvoiceEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (!invoiceToEmail) return;
    const emails = emailRecipientsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) return;

    setSendingInvoiceEmail(true);
    try {
      await billingApi.emailInvoice(invoiceToEmail.id, emails, company?.id);
      setInvoiceToEmail(null);
      await loadBillingData();
      pushToast({
        tone: 'success',
        title: `Invoice ${invoiceToEmail.invoice_number} emailed`,
        description: `Sent to ${emails.join(', ')}`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not email invoice',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSendingInvoiceEmail(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Billing, Subscriptions &amp; Licenses
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage your Sync subscription plan, project licenses, billing contacts (PBR), and
            downloadable invoices.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => loadBillingData()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="w-4 h-4" />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={handleOpenHostedPaymentPage}
            className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3.5 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <IconCreditCard className="w-4 h-4" />
            <span>Update Payment Method</span>
          </button>

          <button
            type="button"
            onClick={() => setCreateLicenseOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
          >
            <IconPlus className="w-4 h-4" />
            <span>Provision License</span>
          </button>
        </div>
      </div>

      {/* Entitlement & Plan Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <p className="text-xs font-medium text-slate-500">Entitlement Status</p>
          <div className="mt-2 flex items-center gap-2">
            {billingStatus?.has_valid_entitlement ? (
              <Badge variant="success" dot>
                Active Entitlement
              </Badge>
            ) : (
              <Badge variant="warning" dot>
                Trial / Pending Setup
              </Badge>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Card on file: <strong>{billingStatus?.is_credit_card_exist ? 'Yes' : 'No'}</strong>
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <p className="text-xs font-medium text-slate-500">Active &amp; Trial Licenses</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {(billingStatus?.active_licenses_count || 0) +
              (billingStatus?.trial_licenses_count || 0) || licenses.length}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {billingStatus?.active_licenses_count || 0} paid ·{' '}
            {billingStatus?.trial_licenses_count || 0} free trial
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <p className="text-xs font-medium text-slate-500">Current Subscription Plan</p>
          <p className="mt-1 text-lg font-bold text-indigo-600">
            {activeSub ? activeSub.plan_code : 'Standard Trial'}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {activeSub
              ? `${activeSub.currency} ${activeSub.amount} / ${activeSub.billing_interval}`
              : 'No recurring subscription'}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <p className="text-xs font-medium text-slate-500">Outstanding Balance Due</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            ${billingStatus?.total_balance_due || '0.00'}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {billingStatus?.unpaid_invoices_count || 0} unpaid invoice(s)
          </p>
        </div>
      </div>

      {/* Subscription Details & Watchers */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-bold text-slate-900">Subscription Management</h2>
              <p className="text-xs text-slate-500">
                Manage recurring billing renewal, cancellation, and subscription event watchers.
              </p>
            </div>
            {activeSub && (
              <Badge
                variant={
                  activeSub.status === 'live'
                    ? 'success'
                    : activeSub.status === 'trial'
                      ? 'info'
                      : 'warning'
                }
                dot
              >
                {activeSub.status}
              </Badge>
            )}
          </div>

          {activeSub ? (
            <div className="rounded-xl bg-slate-50 p-4 border border-slate-100 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Plan Code:</span>
                <span className="font-mono font-semibold text-slate-900">
                  {activeSub.plan_code}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">External Subscription ID:</span>
                <span className="font-mono text-slate-700">
                  {activeSub.external_subscription_id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Current Billing Term:</span>
                <span className="text-slate-700">
                  {formatDateTime(activeSub.current_term_start)} →{' '}
                  {formatDateTime(activeSub.current_term_end)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Auto-Renewal State:</span>
                <span className="font-semibold text-slate-800">
                  {activeSub.cancel_at_period_end
                    ? 'Cancels at period end'
                    : 'Auto-renews at term end'}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
              No active subscription record found. Provision a standard license or reactivate below.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancelOrReactivateSubscription}
              className={`rounded-xl px-4 py-2 text-xs font-semibold ${
                activeSub && !activeSub.cancel_at_period_end && activeSub.status !== 'cancelled'
                  ? 'border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              {activeSub && !activeSub.cancel_at_period_end && activeSub.status !== 'cancelled'
                ? 'Cancel Subscription at Period End'
                : 'Reactivate Subscription'}
            </button>

            <form onSubmit={handleAddWatcher} className="flex items-center gap-2">
              <input
                type="email"
                value={watcherEmailInput}
                onChange={(e) => setWatcherEmailInput(e.target.value)}
                placeholder="Add watcher email…"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
              />
              <button
                type="submit"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Watch
              </button>
            </form>
          </div>

          {activeSub?.watchers && activeSub.watchers.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-600 mb-2">Subscription Watchers:</p>
              <div className="flex flex-wrap gap-2">
                {activeSub.watchers.map((w) => (
                  <span
                    key={w.email}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
                  >
                    <span>{w.email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveWatcher(w.email)}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Billing Contacts & Primary Billing Receiver (PBR) */}
        <div className="lg:col-span-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs space-y-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Primary Billing Receiver (PBR) &amp; Contacts
            </h2>
            <p className="text-xs text-slate-500">
              Manage users authorized to receive invoices and manage company billing.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <ul className="divide-y divide-slate-200 text-xs">
              {billingUsers.length === 0 ? (
                <li className="p-4 text-center text-slate-500">
                  No billing users configured yet. Add a user below.
                </li>
              ) : (
                billingUsersPagination.pageItems.map((bu) => (
                  <li key={bu.id} className="flex items-center justify-between gap-2 p-3">
                    <div>
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        <span>
                          {[bu.first_name, bu.last_name].filter(Boolean).join(' ') || bu.email}
                        </span>
                        {bu.is_primary && <Badge variant="purple">Primary PBR</Badge>}
                      </div>
                      <div className="text-slate-500">{bu.email}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!bu.is_primary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimaryBillingUser(bu.id)}
                          className="rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          Make Primary
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveBillingContact(bu.id)}
                        className="rounded border border-rose-200 bg-rose-50 p-1 text-rose-700 hover:bg-rose-100"
                        title="Remove billing permission"
                      >
                        <IconTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
            {billingUsers.length > 0 && (
              <PaginationBar
                pagination={billingUsersPagination}
                itemLabel="contacts"
                compact
              />
            )}
          </div>

          {companyUsers.length > 0 && (
            <form onSubmit={handleAddBillingContact} className="flex items-center gap-2">
              <select
                value={selectedAddBillingUserId}
                onChange={(e) => setSelectedAddBillingUserId(e.target.value)}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs"
              >
                <option value="">-- Grant billing access to team member --</option>
                {companyUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} ({u.email})
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!selectedAddBillingUserId}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                Add
              </button>
            </form>
          )}

          <form onSubmit={handleSaveExtraReceivers} className="space-y-2 border-t border-slate-100 pt-3">
            <label className="block text-xs font-semibold text-slate-700">
              Extra Invoice Email Receivers (comma-separated)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={extraReceiversInput}
                onChange={(e) => setExtraReceiversInput(e.target.value)}
                placeholder="ap@company.com, accounting@company.com"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
              />
              <button
                type="submit"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Save CC
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Licenses Table */}
      <section className="cv-section rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 id="company-licenses-heading" className="text-base font-bold text-slate-900">
              Company Licenses ({licenses.length})
            </h2>
            <p className="text-xs text-slate-500">
              Project and company-level licenses.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              aria-label="Search company licenses"
              value={licenseSearch}
              onChange={(e) => {
                setLicenseSearch(e.target.value);
                licensesPagination.setPage(0);
              }}
              placeholder="Search SKU, ID, project, or status…"
              className="w-full sm:w-64 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
            />
            <select
              aria-label="Filter licenses by status"
              value={licenseStatusFilter}
              onChange={(e) => {
                setLicenseStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                licensesPagination.setPage(0);
              }}
              className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 focus:border-indigo-500 focus:outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active / Trial</option>
              <option value="inactive">Inactive / Expired</option>
            </select>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div
            tabIndex={0}
            role="region"
            aria-labelledby="company-licenses-heading"
            className="table-scroll-container"
          >
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">License SKU</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Qty</th>
                  <th className="px-4 py-3">Trial Ends</th>
                  <th className="px-4 py-3">Expires At</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                      Loading licenses…
                    </td>
                  </tr>
                ) : filteredLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                      {licenses.length === 0
                        ? 'No licenses found. Click “Provision License” to create a standard license.'
                        : 'No licenses match your current search or status filter.'}
                    </td>
                  </tr>
                ) : (
                  licensesPagination.pageItems.map((lic) => (
                    <tr key={lic.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900 tabular-nums">
                        {lic.sku}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            lic.status === 'active'
                              ? 'success'
                              : lic.status === 'free_trial'
                                ? 'info'
                                : 'warning'
                          }
                          dot
                        >
                          {lic.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-semibold tabular-nums">{lic.quantity}</td>
                      <td className="px-4 py-3 text-slate-600 tabular-nums">
                        {formatDateTime(lic.trial_ends_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 tabular-nums">
                        {formatDateTime(lic.expires_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 tabular-nums">
                        {formatDateTime(lic.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar pagination={licensesPagination} itemLabel="licenses" />
        </div>
      </section>

      {/* Invoices Table */}
      <section className="cv-section rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs space-y-4">
        <div>
          <h2 id="invoices-history-heading" className="text-base font-bold text-slate-900">
            Invoices &amp; Billing History ({invoices.length})
          </h2>
          <p className="text-xs text-slate-500">
            View line items, download/print PDF invoices, or email invoices to accounting contacts.
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div
            tabIndex={0}
            role="region"
            aria-labelledby="invoices-history-heading"
            className="table-scroll-container"
          >
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Issued</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Balance Due</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                      No invoices issued yet for this company.
                    </td>
                  </tr>
                ) : (
                  invoicesPagination.pageItems.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900 tabular-nums">
                        {inv.invoice_number}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            inv.status === 'paid'
                              ? 'success'
                              : inv.status === 'overdue'
                                ? 'danger'
                                : 'warning'
                          }
                          dot
                        >
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600 tabular-nums">
                        {formatDateTime(inv.issued_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 tabular-nums">
                        {formatDateTime(inv.due_at)}
                      </td>
                      <td className="px-4 py-3 font-mono font-semibold tabular-nums">
                        {inv.currency} {inv.total}
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-700 tabular-nums">
                        {inv.currency} {inv.balance_due}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPreviewInvoice(inv)}
                            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            View / PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setInvoiceToEmail(inv);
                              setEmailRecipientsInput(user?.email || '');
                            }}
                            className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                          >
                            Email Invoice
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar pagination={invoicesPagination} itemLabel="invoices" />
        </div>
      </section>

      {/* Dialog: Provision Standard License */}
      <Dialog
        open={createLicenseOpen}
        onClose={() => setCreateLicenseOpen(false)}
        title="Provision Standard License"
        subtitle="Create a new license for your company."
      >
        <form onSubmit={handleProvisionLicense} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">License SKU</label>
            <select
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-mono"
            >
              <option value="SYNC-PRO-ANNUAL">SYNC-PRO-ANNUAL (Enterprise Annual)</option>
              <option value="SYNC-STD-MONTHLY">SYNC-STD-MONTHLY (Standard Monthly)</option>
              <option value="SYNC-PROJECT-PACK">SYNC-PROJECT-PACK (Multi-Project Pack)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Seat / Project Quantity
            </label>
            <input
              type="number"
              min={1}
              max={500}
              required
              value={quantityInput}
              onChange={(e) => setQuantityInput(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateLicenseOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingLicense}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingLicense ? 'Provisioning…' : 'Provision License'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Dialog: Email Invoice */}
      <Dialog
        open={Boolean(invoiceToEmail)}
        onClose={() => setInvoiceToEmail(null)}
        title={`Email Invoice ${invoiceToEmail?.invoice_number || ''}`}
        subtitle="Send this invoice to one or more accounting email addresses."
      >
        <form onSubmit={handleSendInvoiceEmail} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Recipient Email Addresses (comma-separated)
            </label>
            <input
              type="text"
              required
              value={emailRecipientsInput}
              onChange={(e) => setEmailRecipientsInput(e.target.value)}
              placeholder="billing@company.com, controller@company.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setInvoiceToEmail(null)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sendingInvoiceEmail}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {sendingInvoiceEmail ? 'Sending…' : 'Send Invoice Email'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Dialog: Printable Invoice View */}
      <Dialog
        open={Boolean(previewInvoice)}
        onClose={() => setPreviewInvoice(null)}
        title={`Invoice ${previewInvoice?.invoice_number || ''}`}
        subtitle={`Issued ${formatDateTime(previewInvoice?.issued_at)}`}
        maxWidthClass="max-w-2xl"
      >
        {previewInvoice && (
          <div className="space-y-4 text-xs">
            <div className="flex justify-between border-b border-slate-200 pb-4">
              <div>
                <p className="font-bold text-slate-900 text-sm">
                  {company?.name || 'Sync Customer'}
                </p>
                <p className="text-slate-500">Invoice #{previewInvoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <Badge variant={previewInvoice.status === 'paid' ? 'success' : 'warning'}>
                  {previewInvoice.status.toUpperCase()}
                </Badge>
                <p className="mt-1 font-mono font-bold text-sm text-slate-900">
                  {previewInvoice.currency} {previewInvoice.total}
                </p>
              </div>
            </div>

            <table className="min-w-full divide-y divide-slate-200 text-left">
              <thead>
                <tr className="text-slate-500 uppercase">
                  <th className="py-2">Description</th>
                  <th className="py-2">Qty</th>
                  <th className="py-2">Unit Price</th>
                  <th className="py-2 text-right">Line Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(previewInvoice.line_items || []).map((li, idx) => (
                  <tr key={idx}>
                    <td className="py-2 font-medium text-slate-800">{li.description}</td>
                    <td className="py-2">{li.quantity}</td>
                    <td className="py-2 font-mono">{li.unit_price}</td>
                    <td className="py-2 text-right font-mono font-semibold">{li.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <a
                href={
                  previewInvoice.pdf_url ||
                  billingApi.getPrintInvoicePdfUrl(previewInvoice.id, company?.id)
                }
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50"
              >
                Download Invoice PDF
              </a>
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-lg bg-indigo-600 px-4 py-1.5 font-semibold text-white hover:bg-indigo-700"
              >
                Print / Save as PDF
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
